import { useCallback, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';


import { Button } from '@/shared/components/ui/Button';
import { UserSearchForm } from '@/features/users/components/UserSearchForm';
import { UsersTable } from '@/features/users/components/UsersTable';
import { useUserActions, useUsers } from '@/features/users/hooks/useUsers';
import type { UserSearchParams } from '@/features/users/types/user';

export const Route = createFileRoute('/_auth/users/')({
  component: UsersListPage,
});

function UsersListPage() {
  // Search/filter state
  const [searchParams, setSearchParams] = useState<UserSearchParams>({
    page: 0,
    size: 20,
    sortBy: 'user_id',
    sortDirection: 'ASC',
  });

  // Fetch users with current params
  const { data, isLoading, isError, error } = useUsers(searchParams);

  // User actions
  const { deleteUser, isDeleting } = useUserActions();

  // Handle search form submission
  const handleSearch = useCallback((params: UserSearchParams) => {
    setSearchParams((prev) => ({
      ...prev,
      ...params,
      page: 0, // Reset to first page on new search
    }));
  }, []);

  // Handle search reset
  const handleReset = useCallback(() => {
    setSearchParams({
      page: 0,
      size: 20,
      sortBy: 'user_id',
      sortDirection: 'ASC',
    });
  }, []);

  // Handle sorting
  const handleSort = useCallback((column: string) => {
    setSearchParams((prev) => ({
      ...prev,
      sortBy: column,
      sortDirection:
        prev.sortBy === column && prev.sortDirection === 'ASC' ? 'DESC' : 'ASC',
    }));
  }, []);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setSearchParams((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  // Handle delete user
  const handleDeleteUser = useCallback(
    (userId: number) => {
      deleteUser(userId);
    },
    [deleteUser]
  );

  // Default pagination for error/loading states
  const defaultPagination = {
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    pageSize: 20,
    hasNext: false,
    hasPrevious: false,
  };

  return (
    <div className="container mx-auto px-6 py-8">
      {/* Main Content */}
      <div className="flex gap-6">
        {/* Sidebar - Search Form */}
        <aside className="w-72 shrink-0">
          <UserSearchForm
            initialValues={searchParams}
            onSearch={handleSearch}
            onReset={handleReset}
          />
        </aside>

        {/* Main - Users Table */}
        <main className="flex-1 min-w-0">
          {isError ? (
            <div className="rounded-md border border-destructive bg-destructive/10 p-6 text-center">
              <p className="text-destructive">
                Error loading users: {error?.message || 'Unknown error'}
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => handleReset()}
              >
                Try Again
              </Button>
            </div>
          ) : (
            <UsersTable
              users={data?.users || []}
              pagination={data?.pagination || defaultPagination}
              isLoading={isLoading}
              sortBy={searchParams.sortBy}
              sortDirection={searchParams.sortDirection}
              onSort={handleSort}
              onPageChange={handlePageChange}
              onDeleteUser={handleDeleteUser}
              isDeleting={isDeleting}
            />
          )}
        </main>
      </div>
    </div>
  );
}
